<script setup lang="ts">
import { motion } from "motion-v";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useTableStore } from "#/store/table";
import { useThemeStore } from "#/store/theme";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";

const MAX_ITEMS = 3;

const props = defineProps<{ decoration?: string }>();
const emit = defineEmits<(e: "goToCart") => void>();

const router = useRouter();

const storeMain = useMainStore();
const tableStore = useTableStore();
const themeStore = useThemeStore();
const decor = useDecorationStore().screen(props.decoration);

const { totalPrice_lib, cartProducts_lib, cartBasketBumpKey } = storeToRefs(storeMain);

const price = computed(() => totalPrice_lib.value + (tableStore.order?.remaining_amount || 0));

const productsCount = computed(() => cartProducts_lib.value?.length || 0);

const maxItems = computed(() => decor.limit("cart_button_products", MAX_ITEMS));
const hasMore = computed(() => maxItems.value > 0 && productsCount.value > maxItems.value);

const products = computed(() => {
	if (!productsCount.value) return [];

	return cartProducts_lib.value
		.slice(0, hasMore.value ? maxItems.value - 1 : productsCount.value)
		.map((product) => ({ src: product.image_link || themeStore.data?.fallback_image || "", id: product.idSystem || product.id }));
});

const iconStyle = computed(() => {
	const icon = decor.icon("cart_button_icon");
	if (!icon) return undefined;

	const size = typeof icon.size === "number" ? `${icon.size}px` : icon.size;
	return { ...(icon.url && { backgroundImage: `url(${JSON.stringify(icon.url)})` }), ...(size && { width: size }) };
});

const onButtonCLick = () => {
	if (tableStore.isTableService && tableStore.hasOrder && !cartProducts_lib.value?.length) return router.push({ name: "order-ts" });
	emit("goToCart");
};
</script>

<template>
	<div class="button_price_bg" v-bind="decor.bind('cart_button', { empty: productsCount === 0 })">
		<div class="wrapper_container_price_bg">
			<div class="button_price" v-bind="decor.bind('cart_button_frame')">
				<div v-if="products.length > 0 && decor.visible('cart_button_products')" class="image_product_container" v-bind="decor.bind('cart_button_products')">
					<div v-for="product in products" :key="product.id" class="filter_image_product" v-bind="decor.bind('filter_image_product')">
						<img :src="product.src" class="img_product" crossorigin="anonymous" alt="" v-bind="decor.bind('filter_image_product_img')">
					</div>
					<div
						v-if="hasMore"
						class="filter_image_product count_product"
						v-bind="decor.bind('filter_image_product_count')"
					>+<AnimNum :price="productsCount - maxItems + 1" /></div>
				</div>
				<motion.div
					:key="cartBasketBumpKey"
					class="button_default_main"
					data-retail-basket-target
					:initial="{ scale: 1 }"
					:animate="cartBasketBumpKey === 0 ? { scale: 1 } : { scale: [1, 1.1, 1] }"
					:transition="{ duration: 0.38, ease: 'easeOut' }"
					:whilePress="{ scale: 0.92 }"
					v-bind="decor.bind('cart_button_pill')"
					@click="onButtonCLick"
				>
					<div class="icont_cart" v-bind="decor.bind('cart_button_icon')" :style="iconStyle"></div>
					<p v-bind="decor.bind('cart_button_total')">
						<AnimNum :price="price" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</motion.div>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
@use './basketButton.scss';
</style>
